/**
 * V5 对比组件
 * 
 * 这个自定义元素渲染一个产品对比部分。
 * 它允许用户并列对比不同的产品，
 * 切换产品分类，并筛选以仅显示差异。
 */
import { getCompareGoodsList, getCompareDetail } from "@utils/api.js";
import { calcTop } from '@utils/index';

if (!customElements.get("v5-compare")) {
  class V5Compare extends HTMLElement {
    constructor() {
      super();
      // 组件状态数据
      this.data = {
        productTypes: [], // 产品分类/类型列表
        selectProductTypeId: "", // 当前选中的产品分类 ID
        selectProductList: [], // 当前正在对比的产品列表
        openAutoDiscountPrice: false, // 是否开启自动折扣价格
        detailLoading: false, // 详情加载状态
        productList: [], // 下拉选择框中的可用产品列表
        skuIdList: [], // 当前选中用于对比的 SKU ID 列表
        top: calcTop(), // 计算出的顶部位置 (可能是初始偏移量)
      };
      this.showDiff = false; // 标志位：是否开启"只看不同"模式
      this._initialized = false; // 初始化标志，防止重复初始化
      this.isMobile = window.innerWidth < 1024; // 移动端设备检测
      this.DOM = {}; // DOM 元素缓存
      this.handleDocumentClick = this.handleDocumentClick.bind(this); // 绑定点击处理函数
    }

    /**
     * 生命周期回调：当元素被添加到文档时调用。
     * 初始化 DOM 元素、数据和事件监听器。
     */
    async connectedCallback() {
      if (this._initialized) return;
      
      // 缓存 DOM 元素
      this.DOM = {
        headerItems: this.querySelectorAll(".com-header-item"),
        currProducts: this.querySelector(".curr-products"),
        productSelect: this.querySelector(".product-select"),
        productInfo: this.querySelector(".product-info"),
        compareDetails: this.querySelector(".compare-details"),
      };
      
      // 获取文本属性或使用默认值
      this.text = {
        shop_now: window.pageInfo?.strings?.product?.shopNow,
        show_diff: window.pageInfo?.strings?.product?.showDiffText,
      };
      
      // 解析配置属性
      this.data.openAutoDiscountPrice = this.getAttribute("open-auto-discount-price") === "true";

      // 从嵌入的 JSON-LD 解析产品类型
      const el = this.querySelector('script[type="application/ld+json"]');
      this.data.productTypes = JSON.parse(el.textContent) || [];
      if (!this.data.productTypes.length) return;

      // 渲染初始骨架屏
      this.renderSkeleton()
      
      // 确定初始选中的产品类型
      this.data.selectProductTypeId = this.getCurentType() || this.data.productTypes[0].id;
      
      // 设置头部项目的激活状态
      this.DOM.headerItems.forEach((item) => {
        if (item.dataset.id === this.data.selectProductTypeId) {
          item.classList.add("active");
        }
      });

      // 获取初始数据
      await this.getDetail()
      await this.getGoodsList()
      
      this._initialized = true;
      this.bindEvents();

      // 初始化产品选择栏的吸顶行为
      if (this.DOM.currProducts) {
        this.initStickyObserver();
      }
    }

    /**
     * 初始化产品选择栏的吸顶观察者。
     * 使用 scroll 事件来切换 sticky 样式。
     */
    initStickyObserver() {
      // 使用 scroll 事件监听 sticky 状态
      // 通过检测元素是否处于 "sticky" 状态（top 值是否等于设定的阈值）来切换样式
      this._handleScroll = this._handleScroll.bind(this);
      window.addEventListener('scroll', this._handleScroll, { passive: true });
      // 初始化检查一次
      this._handleScroll();
    }

    /**
     * 吸顶定位逻辑的滚动处理程序。
     * 根据元素相对于头部的位置更新 'is-sticky' 类。
     */
    _handleScroll() {
      if (!this.DOM.currProducts) return;
      
      const rect = this.DOM.currProducts.getBoundingClientRect();
      // 动态获取 Header 高度，并实时更新 sticky 的 top 阈值
      const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue("--jky-header-h")) || 0;
      const stickyTop = headerHeight || 0;
      
      // 使用滞后（Hysteresis）逻辑防止闪烁
      // 当前是 sticky 状态时，使用更宽松的阈值（+5px）才解除
      // 当前非 sticky 状态时，使用标准阈值（+1px）进入
      const isStickyNow = this.DOM.currProducts.classList.contains('is-sticky');
      const threshold = isStickyNow ? stickyTop + 5 : stickyTop + 1;
      
      const isStuck = rect.top <= threshold;

      if (isStuck) {
        if (!this.DOM.currProducts.classList.contains('is-sticky')) {
          this.DOM.currProducts.classList.add('is-sticky');
        }
      } else {
        if (this.DOM.currProducts.classList.contains('is-sticky')) {
          this.DOM.currProducts.classList.remove('is-sticky');
        }
      }
    }

    /**
     * 生命周期回调：当元素从文档中移除时调用。
     * 清理事件监听器。
     */
    disconnectedCallback() {
      if (this._handleScroll) {
        window.removeEventListener('scroll', this._handleScroll);
      }
      document.removeEventListener('click', this.handleDocumentClick);
    }

    /**
     * 根据 URL 的 'handle' 参数确定当前产品类型 ID。
     * @returns {string} 产品类型 ID 或空字符串
     */
    getCurentType() {
      const handle = new URLSearchParams(window.location.search).get("handle");
      return handle ? this.data.productTypes.find((item) => item.handle === handle)?.id || "" : "";
    }

    /**
     * 绑定所有用户交互事件（点击、切换）。
     */
    bindEvents() {
      // 头部项目点击（切换分类）
      this.DOM.headerItems.forEach((item) => {
        item.addEventListener("click", this.handleClickHeaderItem.bind(this));
      });

      // 对比详情的手风琴切换事件
      this.DOM.compareDetails.addEventListener("click", (e) => {
        const header = e.target.closest(".compare-header");
        if (header) {
          const section = header.closest(".compare-section");
          if (section) {
            const content = section.querySelector(".compare-content");
            if (header.classList.contains("active")) {
              header.classList.remove("active");
              content.style.maxHeight = "0px";
            } else {
              header.classList.add("active");
              content.style.maxHeight = content.scrollHeight + "px";
            }
          }
        }
      });
      
      // 产品选择事件
      this.DOM.currProducts?.addEventListener("click", (e) => {
        // 显示差异切换
        const toggleWrapper = e.target.closest(".toggle-diff-wrapper");
        if (toggleWrapper) {
          e.preventDefault();
          this.showDiff = !this.showDiff;
          this.getDetail();
        }

        // 选择框点击（打开下拉菜单）
        const selectInput = e.target.closest('.select-input');
        if(!selectInput) return
        const parentItem = selectInput.closest('.select-item');
        if (!parentItem) return;

        // 检查当前是否已经激活
        const isActive = parentItem.classList.contains('active');

        // 清除其他激活状态（如果有多个 select-item）
        this.DOM.productSelect.querySelectorAll('.select-item.active')?.forEach(el => {
            el.classList.remove('active');
        });
        // 给当前 selectInput 的父级元素添加 active
        if (!isActive) parentItem.classList.add('active');
      });

      // 点击空白处：关闭当前激活的元素
      document.addEventListener('click', this.handleDocumentClick);

      // 下拉选项选择
      this.addEventListener('click', async (e) => {
          const selectOption = e.target.closest('.select-option');
          if (!selectOption) return
          const { sku_id, index } = selectOption.dataset
          const skuId = Number(sku_id)
          // 如果在相同位置选择了相同的 SKU，则不进行操作
          if (this.data.skuIdList[index] === skuId) return
          
          // 如果 SKU 已经在其他位置被选中，则交换它们
          if (this.data.skuIdList.includes(skuId)) {
              const j = this.data.skuIdList.findIndex(item => item === skuId)
              const id = this.data.skuIdList[j]
              this.data.skuIdList[j] = this.data.skuIdList[index]
              this.data.skuIdList[index] = id
          } else {
              // 否则，设置新的 SKU
              this.data.skuIdList[index] = skuId
          }
          // 使用新选择刷新详情
          await this.getDetail()
      })
    }

    /**
     * 文档点击处理程序，用于在点击外部时关闭下拉菜单。
     * @param {Event} e 
     */
    handleDocumentClick(e) {
      const activeItem = this.DOM.productSelect.querySelector('.select-item.active');
      if (!activeItem) return;
      // 如果点击不在 activeItem 内 → 关闭
      if (!activeItem.contains(e.target)) {
          activeItem.classList.remove('active');
      }
    }

    /**
     * 处理从顶部头部切换产品分类。
     * @param {Event} e 
     */
    async handleClickHeaderItem(e) {
      const item = e.target.closest(".com-header-item");
      if (!item) return;
      if(item.classList.contains('active')) return
      
      // 更新激活的标签
      this.DOM.headerItems.forEach((item) => {
        item.classList.remove("active");
      });
      item.classList.add("active");
      
      // 更新状态并重新获取
      this.data.selectProductTypeId = item.dataset.id;
      this.data.skuIdList = [] // 切换类型时重置选择
      this.detailLoading = true
      this.renderSkeleton();
      await this.getDetail({ skipRenderProductList: true });
      await this.getGoodsList()
      this.detailLoading = false
    }

    /**
     * 从 API 获取对比详情。
     * @param {Object} options 选项对象
     * @param {boolean} options.skipRenderProductList 是否跳过渲染产品下拉列表
     */
    async getDetail(options = {}) {
      const { skipRenderProductList = false } = options;
        const { data } = await getCompareDetail({
          skuIdList: this.data.skuIdList,
          showDiff: this.showDiff,
          pageType: 3,
          platformType: this.isMobile ? 2 : 1,
          pageNum: this.data.selectProductTypeId,
        });
        
        // 更新数据
        this.data.selectProductList = data.fourProductResps || [];
        // 渲染选中的产品头部
        this.DOM.productSelect.innerHTML = this.renderProductSelect();
        if (this.DOM.productInfo) this.DOM.productInfo.innerHTML = this.renderProductInfo();
        
        this.data.detailList = data.dictInfoResps || [];
        // 渲染对比表格
        this.DOM.compareDetails.innerHTML = this.renderCompareDetails();
        
        // 如果需要，重新渲染下拉列表（确保选项匹配当前上下文）
        if (!skipRenderProductList && this.data.productList.length) this.renderProductList()
    }

    /**
     * 生成"只看不同"切换按钮的 HTML。
     * @returns {string} HTML 字符串
     */
    renderToggleBtn() {
      return `
        <div class="col-span-2 lg:col-span-4 flex justify-center items-center lg:pt-6 pb-4 bg-white toggle-diff">
           <div class="flex items-center cursor-pointer toggle-diff-wrapper">
               <div class="relative inline-flex items-center cursor-pointer">
                 <input type="checkbox" class="sr-only peer toggle-diff-checkbox" ${
                   this.showDiff ? "checked" : ""
                 }>
                 <div class="w-11 h-6 bg-black/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--jds-color-primary)]"></div>
               </div>
               <span class="ml-3 lg:ml-4 text-sm lg:text-xl font-semibold text-[#242F20] select-none">${
                 this.text.show_diff
               }</span>
           </div>
        </div>
      `;
    }

    /**
     * 在获取数据时渲染骨架屏加载器。
     */
    renderSkeleton() {
        const num = this.isMobile ? 2 : 3
        const selectSkeleton = Array(num).fill(0).map(() => `
            <div class="lg:pl-6 lg:pr-[4.5rem] animate-pulse">
                <div class="w-full h-12 bg-[#F5F5F5] rounded mb-3"></div>
            </div>
        `).join('');

        const infoSkeleton = Array(num).fill(0).map(() => `
            <div class="lg:pl-6 lg:pr-[4.5rem] animate-pulse">
                <div class="w-full h-[6.25rem] bg-[#F5F5F5] rounded mb-4"></div>
                <div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div>
                <div class="h-4 bg-[#F5F5F5] rounded w-1/2 mb-4"></div>
                <div class="h-8 bg-[#F5F5F5] rounded w-1/3"></div>
            </div>
        `).join('');

        const checkoutSkeleton = `
          <div class="col-span-2 lg:col-span-4 flex justify-center items-center lg:pt-6 pb-6 bg-white animate-pulse">
            <div class="flex items-center justify-center w-full">
              <div class="w-11 h-6 bg-[#F5F5F5] rounded-full"></div>
              <div class="ml-3 lg:ml-4 h-6 lg:h-7 bg-[#F5F5F5] rounded w-24 lg:w-48"></div>
            </div>
          </div>
        `
        this.DOM.productSelect.innerHTML = (this.isMobile ? selectSkeleton : `<div class="hidden lg:block"></div>${selectSkeleton}`);
        if (this.DOM.productInfo) {
          this.DOM.productInfo.innerHTML = (this.isMobile ? infoSkeleton + checkoutSkeleton : `<div class="hidden lg:block"></div>${infoSkeleton + checkoutSkeleton}`);
        }
        const detailsSkeleton = Array(2).fill(0).map(() => `
            <div class="compare-section animate-pulse">
                <div class="compare-header bg-[#E8E8E5] h-[3.75rem] px-4 lg:px-6 flex items-center justify-between border-b border-solid border-[#E2E3E3]">
                    <div class="h-6 w-1/3 bg-[#dcdcdc] rounded"></div>
                </div>
                <div class="compare-content border-t border-[#E2E3E3] bg-white">
                    <div class="grid grid-cols-[10.25rem_10.25rem] gap-4 lg:gap-0 lg:grid-cols-[24rem_24rem_24rem] lg:pl-[10.5rem] border-b border-[#E2E3E3]">
                        <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        <div class="p-6 min-h-[5rem]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                    </div>
                     <div class="grid grid-cols-[10.25rem_10.25rem] gap-4 lg:gap-0 lg:grid-cols-[24rem_24rem_24rem] lg:pl-[10.5rem]">
                        <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        <div class="p-6 min-h-[5rem]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                    </div>
                </div>
            </div>
        `).join('');
        this.DOM.compareDetails.innerHTML = detailsSkeleton;
    }

    /**
     * 渲染产品选择下拉。
     * @returns {string} HTML 字符串
     */
    renderProductSelect() {
      if (this.data.selectProductList.length === 0) return "";
      let html = this.isMobile ? "" : '<div class="hidden lg:block"></div>';
      this.data.selectProductList.forEach((item, index) => {
        const { title, skuId } = item;
        
        html += `
            <div class="lg:pl-6 lg:pr-[4.5rem]">
              <div class="relative w-full h-full select-item">
                  <div class="h-full flex items-center border bg-[#fff] border-[#E2E3E3] border-solid py-2 pl-2 pr-8 cursor-pointer select-input 
                      after:content-[''] after:w-2 after:h-2 after:border-r-2 after:border-b-2 after:border-t-0 after:border-l-0 after:border-solid after:border-current after:rotate-45 after:absolute after:top-1/2 after:right-4 after:-translate-y-1/2 after:transition-transform after:duration-300 after:ease-in-out after:pointer-events-none" 
                      data-index="${index}"
                  >
                      <span class="pointer-events-none font-semibold text-xs lg:text-base text-[#242F20]">
                        ${title}
                      </span>
                  </div>
                  <div class="bg-[#fff] border-[#dcdcdc] py-1 border-solid border w-full top-[calc(100%+2px)] left-0 max-h-[20rem] absolute overflow-y-auto invisible opacity-0 select-dropdown z-10" data-sku="${skuId}">
                  </div>
              </div>
            </div>
        `;
      });

      return html;
    }

    renderProductInfo() {
      if (this.data.selectProductList.length === 0) return "";
      let html = this.isMobile ? "" : '<div class="hidden lg:block"></div>';

      this.data.selectProductList.forEach((item) => {
        const {
          skuId,
          goodUrl,
          image,
          variantDesc,
          autoDiscountPrice,
          shopifyFrontprice,
          shopifyFrontOriginalPrice,
        } = item;
        
        const current_price = Math.min(
          autoDiscountPrice || 0,
          shopifyFrontprice || 0
        );
        const origin_price = Math.max(
          autoDiscountPrice || 0,
          shopifyFrontprice || 0,
          shopifyFrontOriginalPrice || 0
        );

        html += `
            <div class="lg:pl-6 lg:pr-[4.5rem]">
                <div class="w-full my-2 lg:mt-3 lg:mb-4 h-[4.875rem] lg:h-[6.25rem] images">
                    <a href="${goodUrl}?variant=${skuId}">
                        <img class="h-[4.875rem] lg:h-[6.25rem] aspect-[1/1]" width="100" height="100" loading="lazy" src="${image}&width=100">
                    </a>
                </div>
                <div class="h-6 lg:h-[2.5rem] text-[0.625rem] leading-3 lg:text-sm font-normal text-[#242F20] mb-3 lg:mb-4 line-clamp-2 desc">${
                  variantDesc || ""
                }</div>
                
                <div class="text-[#242F20] price my-2">
                    <span class="lg:text-xl text-sm font-semibold">
                        ${
                          this.openAutoDiscountPrice && item.autoDiscountPrice > 0
                            ? window.formatMoney(current_price)
                            : window.formatMoney(
                                shopifyFrontprice,
                                window?.pageInfo?.currency?.money_format
                              )
                        }
                    </span>
                    <span class="text-[0.75rem] lg:text-[0.875rem] font-normal line-through pl-1">
                        ${
                          this.openAutoDiscountPrice && item.autoDiscountPrice > 0
                            ? origin_price > current_price
                              ? window.formatMoney(origin_price)
                              : ""
                            : shopifyFrontOriginalPrice &&
                              shopifyFrontOriginalPrice > shopifyFrontprice
                            ? `${window.formatMoney(
                                shopifyFrontOriginalPrice,
                                window.pageInfo.currency.money_format
                              )}`
                            : ""
                        }
                    </span>
                </div>
                <div class=" my-2 shop-now">
                  <a href="${goodUrl}?variant=${skuId}" class="text-primary text-sm lg:text-xl font-semibold  flex items-center">
                    ${this.text.shop_now} &nbsp;<jds-icon name="arrow-right-long"></jds-icon>
                  </a>
                </div>
            </div>
        `;
      });

      html += this.renderToggleBtn();
      return html;
    }

    /**
     * 渲染对比详情表格（手风琴）。
     * @returns {string} HTML 字符串
     */
    renderCompareDetails() {
      if (!Array.isArray(this.data.detailList) || this.data.detailList.length === 0) return "";
      return this.data.detailList.map((l1, index) => {
        const hasChildren = Array.isArray(l1.childInfos) && l1.childInfos.length > 0;
        const hasValues = Array.isArray(l1.showValue) && l1.showValue.length > 0;
        const contentHtml = hasChildren ? this.renderLevel2(l1.childInfos) : hasValues ? this.renderRowPC(l1, true, false)
            : "";
        return `
          <div class="compare-section last:border-b last:border-solid last:border-[#E2E3E3] data-index="${index}">
             <div class="compare-header bg-[#E8E8E5] h-10 lg:h-14 pl-4 lg:pl-5 pr-3 flex items-center justify-between cursor-pointer select-none group active border-b border-solid border-[#E2E3E3]">
                <span class="font-semibold text-base lg:text-2xl text-[#242F20]/[0.9]">${l1.infoShowTitle}</span>
                <span class="transition-transform duration-300 transform group-[.active]:rotate-180">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 9L12 15L18 9" stroke="#00000099" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
             </div>
             <div class="compare-content overflow-hidden transition-all duration-300 border-t border-[#E2E3E3]">
                ${contentHtml}
             </div>
          </div>
        `
      }).join("");
    }

    /**
     * 渲染二级对比行。
     * @param {Array} l2List 二级项目列表
     * @returns {string} HTML 字符串
     */
    renderLevel2(l2List) {
      if (!l2List || l2List.length === 0) return "";

      return l2List
        .map((l2) => {
          const isGroup = l2.childInfos && l2.childInfos.length > 0;
           const hasValues = Array.isArray(l2.showValue) && l2.showValue.length > 0;
            if (isGroup) {
              const rows = l2.childInfos.map((l3) => this.renderRowPC(l3)).join("");
              return `
                 <div class="w-[21.5rem] lg:w-full mx-auto lg:flex border-b border-solid border-[#E2E3E3] last:border-0">
                     <div class="w-full lg:w-[10.5rem] shrink-0 lg:p-6 pt-6 pb-4 lg:flex items-center justify-center font-bold text-[#242F20] text-base lg:text-xl bg-white border-b lg:border-b-0 lg:text-center lg:border-r border-solid border-[#E2E3E3]">
                         ${l2.infoShowTitle}
                     </div>
                     <div class="flex-1">
                         ${hasValues ? this.renderRowPC(l2, true, false) : rows }
                     </div>
                 </div>
              `;
            } else {
              return this.renderRowPC(l2, true);
            }
        })
        .join("");
    }

    /**
     * 渲染 PC/移动端的单行对比值。
     * @param {Object} node 数据节点
     * @param {boolean} isFullWidth 是否全宽行
     * @param {boolean} showTitle 是否在单元格中显示标题（用于移动端布局）
     * @returns {string} HTML 字符串
     */
    renderRowPC(node, isFullWidth = false, showTitle = true) {
      const values = node.showValue || [];
      const gridClass = "w-[21.5rem] lg:w-full mx-auto grid gap-4 lg:gap-0 grid-cols-[10.25rem_10.25rem] lg:grid-cols-[24rem_24rem_24rem]";
      const paddingClass = isFullWidth ? "lg:pl-[10.5rem]" : "";
      const cells = values.map(val => `
            <div class="py-3 pr-4 lg:p-6 min-h-[3.375rem] lg:min-h-[5rem] flex flex-col ">
                ${showTitle ? `<span class="text-[0.75rem] lg:text-[1rem] leading-[1.2] text-[#818181] mb-2 break-words font-normal">${node.infoShowTitle}</span>` : ''}
                <span class="text-[0.75rem] lg:text-[1rem] leading-[1.2] text-[#242F20] whitespace-pre-line break-words font-normal">${val || "一"}</span>
            </div>
        `
        )
        .join("");

      return `
            <div class="${gridClass} lg:border-b border-solid border-[#E2E3E3] last:border-0 ${paddingClass} bg-white">
                ${cells}
            </div>
        `;
    }

    /**
     * 获取下拉菜单的所有可用商品列表。
     */
    async getGoodsList() {
        const { data: { classifyGoodsResps = [], skuIdList = []} } = await getCompareGoodsList({ pageNum: this.data.selectProductTypeId });
        this.data.productList = classifyGoodsResps || [];
        this.data.skuIdList = skuIdList || [];
        this.renderProductList()
    }

    /**
     * 渲染产品下拉选项。
     * 填充 `.select-dropdown` 元素。
     */
    renderProductList() {
      if (!Array.isArray(this.data.productList) || this.data.productList.length === 0) return "";
      const productMap = new Map();
      // 按分类标题分组产品
      this.data.productList.forEach(product => {
          productMap.set(product.classifyTitle, product.fourProductResps);
      });
      
      // 遍历每个选中的产品槽位以填充其下拉菜单
      this.data.selectProductList.forEach((item, index) => {
          const dropdownFragments = [];
          productMap.forEach((options, classifyTitle) => {
              const optionsHtml = options.map(opt => `
                  <div class="relative flex items-center text-[0.875rem] lg:text-[1rem] font-medium py-1.5 pl-3 pr-8 leading-[1.2] cursor-pointer ${item.skuId == opt.skuId ? 'active' : ''} ${this.variantId == opt.skuId ? 'cursor-not-allowed' : ''} select-option" data-classify_title="${classifyTitle}" data-title="${opt.title}" data-sku_id="${opt.skuId}" data-index="${index}" >
                      ${opt.title}
                  </div>
              `).join('');
              dropdownFragments.push(`
                  <div>
                      <div class="text-[#1a1a1a] px-3 text-[1rem] lg:text-[1.25rem] font-semibold py-1.5 group-label">${classifyTitle}</div>
                      ${optionsHtml}
                  </div>
              `);
          })
          const el = this.querySelector(`.select-dropdown[data-sku="${item.skuId}"]`)
          if (el) el.innerHTML = dropdownFragments.join(''); // 将所有分组片段拼接后填充到对应下拉容器
      })
    }
  }
  customElements.define("v5-compare", V5Compare);
}
